import { InputVariant, PhoneInput, PhoneInputProps } from '@pluxee-design-system/core';
import CountryNames from 'common/countries';
import useConfig from 'data/useConfig';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useController } from 'react-hook-form';
import { Country, getCountryCallingCode, parsePhoneNumber } from 'react-phone-number-input';

interface PhoneControlProps
  extends Omit<
    PhoneInputProps,
    | 'countries'
    | 'defaultInputValue'
    | 'defaultSelectValue'
    | 'isDisabled'
    | 'name'
    | 'onChange'
    | 'variant'
  > {
  defaultCountry?: string;
  id?: string; // TODO: not supported
  name: string;
  onChange?: (newValue: string) => void;
  readonly?: boolean;
}

// Pluxee PhoneInput wraps a value with ( )
const normalizeValue = (newValue: string) =>
  parsePhoneNumber(newValue)?.number || newValue.replace('(', '').replace(')', '');
const hasOnlyCountry = (newValue: string, normalizedValue: string) =>
  newValue === `(${normalizedValue})`;
const getCountryCode = (newValue: string) => newValue.split(')')?.[0]?.replace('(', '');

const parseFieldValue = (defaultCountryCode: Country, value: string, keyCount: number) => {
  const parsedInitialValue = parsePhoneNumber(value);
  return {
    keyCount,
    selectValue: `+${
      parsedInitialValue
        ? parsedInitialValue.countryCallingCode
        : getCountryCallingCode(defaultCountryCode)
    }`,
    inputValue: parsedInitialValue ? parsedInitialValue.nationalNumber : value ?? '',
  };
};

// TODO: get rid-of library: react-phone-number-input
const PhoneControl = ({ defaultCountry, name, readonly, ...phoneProps }: PhoneControlProps) => {
  const { field, fieldState, formState } = useController({ name });
  const initialValue = formState.defaultValues?.[name];
  const { onChange } = field;
  const { error, isTouched } = fieldState;
  const hasError = Boolean(error);

  const lastValue = useRef('');
  const keyCount = useRef(0);
  const lastKnownCountryCode = useRef<Country | undefined>(undefined);
  const { callingCodes, isoCode } = useConfig();
  const defaultCountryCode = (defaultCountry ?? isoCode.toUpperCase()) as Country;
  const [defaultValues, setDefaultValues] = useState(() =>
    parseFieldValue(lastKnownCountryCode.current ?? defaultCountryCode, initialValue, 0),
  );
  const countries = useMemo(
    () =>
      (callingCodes as Country[]).map((code) => ({
        name: CountryNames[code] || code,
        dial_code: `+${getCountryCallingCode(code)}`,
        code,
      })),
    [callingCodes],
  );
  const handleChange = useCallback(
    (changedValue: string) => {
      const normalizedValue = normalizeValue(changedValue);
      // hack to stop infinite loop, helpers.setValue is re-created on every field change :/
      if (lastValue.current !== normalizedValue) {
        if (normalizedValue && normalizedValue !== field.value) {
          const countryCallCode = countries.find(
            (c) => c.dial_code === getCountryCode(changedValue),
          )?.code;
          const newValue =
            normalizedValue !== defaultValues.selectValue &&
            !hasOnlyCountry(changedValue, normalizedValue)
              ? normalizedValue
              : '';
          lastKnownCountryCode.current = countryCallCode;
          if (newValue !== lastValue.current) {
            onChange(newValue);
          }
        }
        lastValue.current = normalizedValue;
      }
    },
    [countries, defaultValues.selectValue, onChange, field.value],
  );

  useEffect(() => {
    if (lastValue.current !== field.value) {
      const normalizedValue = normalizeValue(field.value);
      setDefaultValues(
        parseFieldValue(
          lastKnownCountryCode.current ?? defaultCountryCode,
          normalizedValue,
          (keyCount.current += 1),
        ),
      );
    }
  }, [defaultCountryCode, field.value, setDefaultValues]);

  return (
    <PhoneInput
      key={defaultValues.keyCount} // to force re-mount the component because DS is missing useEffect :-/
      {...phoneProps}
      onBlur={field.onBlur}
      onChange={phoneProps.onChange ?? handleChange}
      countries={countries}
      defaultInputValue={defaultValues.inputValue}
      defaultSelectValue={defaultValues.selectValue}
      variant={
        readonly
          ? InputVariant.READONLY
          : hasError
            ? InputVariant.ERROR
            : isTouched || formState.isSubmitted
              ? InputVariant.SUCCESS
              : InputVariant.FILLED
      }
      helpText={hasError ? error?.message : phoneProps.helpText}
    />
  );
};

export default PhoneControl;
